import React, { createContext, useContext, useState, useEffect } from 'react';

const AdminContext = createContext();

export const useAdmin = () => {
  const context = useContext(AdminContext);
  if (!context) {
    throw new Error('useAdmin must be used within an AdminProvider');
  }
  return context;
};

// Simple admin credentials - in production, use environment variables
const ADMIN_CREDENTIALS = {
  username: 'Allykrita940',
  password: 'TokenLocker2024!', // Change this to a secure password
  walletAddress: '0xf686507C4EF14C65148d4eCA6371Db72B957F71c' // Platform fee wallet
};

export const AdminProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [adminInfo, setAdminInfo] = useState(null);

  // Check if user is already authenticated on page load
  useEffect(() => {
    const checkAuth = () => {
      try {
        const authData = localStorage.getItem('adminAuth');
        if (authData) {
          const { username, timestamp } = JSON.parse(authData);
          // Check if session is still valid (24 hours)
          const now = Date.now();
          const sessionDuration = 24 * 60 * 60 * 1000; // 24 hours
          
          if (now - timestamp < sessionDuration) {
            setIsAuthenticated(true);
            setAdminInfo({ username });
          } else {
            // Session expired
            localStorage.removeItem('adminAuth');
          }
        }
      } catch (error) {
        console.error('Error checking admin authentication:', error);
        localStorage.removeItem('adminAuth');
      } finally {
        setIsLoading(false);
      }
    };

    checkAuth();
  }, []);

  const login = async (username, password, walletAddress) => {
    try {
      // Verify credentials
      if (username === ADMIN_CREDENTIALS.username && 
          password === ADMIN_CREDENTIALS.password &&
          walletAddress.toLowerCase() === ADMIN_CREDENTIALS.walletAddress.toLowerCase()) {
        
        // Store authentication data
        const authData = {
          username,
          walletAddress: walletAddress.toLowerCase(),
          timestamp: Date.now()
        };
        
        localStorage.setItem('adminAuth', JSON.stringify(authData));
        setIsAuthenticated(true);
        setAdminInfo({ username, walletAddress: walletAddress.toLowerCase() });
        
        return { success: true };
      } else {
        return { 
          success: false, 
          error: 'Invalid credentials. Please check username, password, and wallet address.' 
        };
      }
    } catch (error) {
      console.error('Login error:', error);
      return { 
        success: false, 
        error: 'Login failed. Please try again.' 
      };
    }
  };

  const logout = () => {
    localStorage.removeItem('adminAuth');
    setIsAuthenticated(false);
    setAdminInfo(null);
  };

  const value = {
    isAuthenticated,
    isLoading,
    adminInfo,
    login,
    logout
  };

  return (
    <AdminContext.Provider value={value}>
      {children}
    </AdminContext.Provider>
  );
};
